// sharedPricingCalculations.js - Frontend pricing module used by all components
// This ensures consistent calculations across all creation/editing flows

// Explicit .js extensions: this module is unit-tested directly by
// scripts/test/marineDuration.test.mjs and platformFee.test.mjs under Node's
// ESM resolver, which does not do extensionless resolution. Both imports are
// zero-import modules. Webpack resolves the exact match fine.
import { isMarineService } from '../../../util/marineService.js';
import { getPlatformFeeRate } from '../../../util/platformFee.js';

// ONE platform rate for every job type, sourced from REACT_APP_PLATFORM_FEE
// (see util/platformFee.js). The server has a single CONFIG.PLATFORM_FEE; the
// earlier dealer/specialist split here was two hardcoded copies of it.
//
// WHO OWNS THE ARITHMETIC (2026-09-03): the SERVER. Everything this module
// computes is for DISPLAY. The LINE payload built by `prepareForSubmission`
// carries INPUTS ONLY — unit_price, quantity, service_discount — and the server
// derives service_amount / service_value / service_fee from them. Two
// implementations of the same formula drifted by real money (request 7601
// billed $110 against a $105 line); one owner cannot. Do not add line-level
// derived pricing back to the payload.
//
// The REQUEST HEADER is different (2026-09-08, production outage). The four
// header columns — Request_Total / Request_Discount / Request_Fee /
// Request_taxes — are NOT NULL, and the server maps the request payload straight
// onto them, so an absent key becomes SQL NULL and the insert/update is
// rejected (ER_BAD_NULL_ERROR on every 9.0 create). Every header write must
// therefore carry all four as NUMBERS: the client's current computation, or 0
// when there are no lines yet. They are schema-satisfying placeholders, not the
// billed figures — the server re-derives all four from the stored lines on
// every line mutation and after every header update, so sending them cannot
// reintroduce the drift. `calculatedTotals` below is that placeholder set.

/**
 * Service-level calculations
 */
export class SharedServicePricing {
  /**
   * Calculate final service amount after discount
   */
  static calculateServiceTotal(service) {
    const amount = parseFloat(service.service_cost || service.service_amount || 0);
    const discount = parseFloat(service.service_discount || 0);
    return Math.max(0, amount - discount);
  }

  /**
   * Validate service discount
   */
  static validateServiceDiscount(service, proposedDiscount) {
    const serviceCost = parseFloat(service.service_cost || service.service_amount || 0);
    const isValid = proposedDiscount >= 0 && proposedDiscount <= serviceCost;
    
    return {
      isValid,
      maxDiscount: serviceCost,
      validatedDiscount: isValid ? proposedDiscount : Math.min(proposedDiscount, serviceCost)
    };
  }

  /**
   * Calculate dealer margin for a service
   */
  static calculateServiceMargin(service) {
    if (!service.retail_price) return 0;
    const retailPrice = parseFloat(service.retail_price || 0);
    const finalAmount = this.calculateServiceTotal(service);
    return Math.max(0, retailPrice - finalAmount);
  }
}

/**
 * Request-level calculations - SAME LOGIC FOR ALL COMPONENTS
 */
export class SharedRequestPricing {
  /**
   * Calculate request total (sum of original service amounts, quantity-aware)
   */
  static calculateRequestTotal(services) {
    return services.reduce((sum, service) => {
      const unitPrice = parseFloat(service.unit_price || service.service_cost || service.service_amount || 0);
      const quantity = parseInt(service.quantity, 10) || 1;
      return sum + (unitPrice * quantity);
    }, 0);
  }

  /**
   * Calculate total discounts across all services
   */
  static calculateRequestDiscount(services) {
    return services.reduce((sum, service) => {
      return sum + parseFloat(service.service_discount || 0);
    }, 0);
  }

  /**
   * Calculate final amount after all discounts
   */
  static calculateRequestFinalAmount(services) {
    return services.reduce((sum, service) => {
      return sum + SharedServicePricing.calculateServiceTotal(service);
    }, 0);
  }

  /**
   * Calculate platform fee based on final amount (NEW: post-discount)
   */
  static calculatePlatformFee(services, jobType = "retail") {
    const finalAmount = this.calculateRequestFinalAmount(services);
    return finalAmount * getPlatformFeeRate();
  }

  /**
   * Calculate the taxable portion of the final amount (excludes non-taxable services)
   */
  static calculateTaxableAmount(services) {
    return services.reduce((sum, service) => {
      if (service.taxable === 0 || service.taxable === false) return sum;
      return sum + SharedServicePricing.calculateServiceTotal(service);
    }, 0);
  }

  /**
   * Calculate taxes on taxable services only (retail jobs only)
   */
  static calculateTaxes(services, taxRate, jobType = "retail") {

    if (jobType === "dealer") return 0; // Dealers don't pay taxes

    const taxableAmount = this.calculateTaxableAmount(services);
    return taxableAmount * (taxRate / 100);
  }

  /**
   * Calculate total duration (quantity-aware)
   *
   * MARINE: an area-priced line's `quantity` is CEIL(square feet), not a unit
   * count, and its `duration_days` is the WHOLE-JOB figure. Scaling it would
   * turn a 187 sq ft hull-sides line at 0.5 days into 93.5 days — the value
   * that feeds `request_duration` and therefore `due_date`, blocking the
   * calendar for three months. The server suppresses this on its side too; this
   * is the client half of the same guard.
   */
  static calculateTotalDuration(services) {
    return services.reduce((sum, service) => {
      const duration = parseFloat(service.duration_days || 0);
      if (isMarineService(service)) return sum + duration;
      const quantity = parseInt(service.quantity, 10) || 1;
      return sum + (duration * quantity);
    }, 0);
  }

  /**
   * Get complete pricing breakdown - MASTER CALCULATION FUNCTION
   */
  static getCompletePricingBreakdown(services, options = {}) {
    const {
      jobType = "retail",
      taxRate = 0
    } = options;

    const breakdown = {
      // Service totals
      originalTotal: this.calculateRequestTotal(services),
      totalDiscount: this.calculateRequestDiscount(services),
      finalAmount: this.calculateRequestFinalAmount(services),
      
      // Fees and taxes (NEW: post-discount)
      platformFee: this.calculatePlatformFee(services, jobType),
      taxes: this.calculateTaxes(services, taxRate, jobType),
      
      // Duration
      totalDuration: this.calculateTotalDuration(services),
      
      // Metadata
      jobType,
      taxRate,
      serviceCount: services.length
    };

    // Add dealer-specific calculations if applicable
    if (jobType === "dealer") {
      const retailTotal = services.reduce((sum, service) => {
        return sum + parseFloat(service.retail_price || service.service_cost || 0);
      }, 0);
      
      breakdown.dealerTotals = {
        wholesaleTotal: breakdown.finalAmount,
        retailTotal: retailTotal,
        totalMargin: retailTotal - breakdown.finalAmount
      };
    }

    // Calculate final customer amount
    breakdown.customerTotal = breakdown.finalAmount + breakdown.taxes;

    return breakdown;
  }
}

/**
 * Utility functions for formatting and display
 */
export class SharedPricingUtils {
  /**
   * Format currency for display
   */
  static formatCurrency(amount) {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  }

  /**
   * Validate pricing data
   */
  static validatePricingData(services) {
    const errors = [];
    
    services.forEach((service, index) => {
      if (!service.service_id) {
        errors.push(`Service ${index}: Missing service_id`);
      }
      if (!service.service_cost && !service.service_amount && !service.unit_price) {
        errors.push(`Service ${index}: Missing service cost`);
      }
      if (service.service_discount < 0) {
        errors.push(`Service ${index}: Invalid discount (negative)`);
      }
      if (service.service_discount > (service.service_cost || service.service_amount || 0)) {
        errors.push(`Service ${index}: Discount exceeds service cost`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Prepare data for backend submission.
   *
   * Returns `services` shaped for the wire (inputs only — see the header note),
   * `calculatedTotals` (duration only; pricing is derived server-side) and the
   * display `breakdown`. The local arithmetic below is kept ONLY to validate
   * the inputs (NaN guards) before they are sent.
   */
  static prepareForSubmission(services, options = {}) {
    const breakdown = SharedRequestPricing.getCompletePricingBreakdown(services, options);
    
    const preparedServices = services.map((service, index) => {
      const quantity = parseInt(service.quantity, 10) || 1;
      const unitPrice = parseFloat(service.unit_price || service.service_cost || service.service_amount || 0);
      const serviceCost = unitPrice * quantity;
      const serviceDiscount = parseFloat(service.service_discount || 0);
      const serviceTotal = serviceCost - serviceDiscount;
      
      // A NEWLY ADDED marine line must have been through the area calculator.
      // Without this it would submit at rate x 1 — $8.50 for a $1,589.50 job —
      // because service_cost on a marine row is a per-sq-ft RATE.
      //
      // Scoped to lines with no `id`: an existing line loaded from the server
      // carries its stored quantity but not this client-only flag, and must not
      // be blocked from being edited.
      if (!service.id && isMarineService(service) && service.marine_area_confirmed !== true) {
        throw new Error(
          `Service ${index}: marine line "${service.description || service.service_id}" has no calculated area. Run the area calculator before saving.`
        );
      }

      // Validate calculations - throw error if we get NaN
      if (isNaN(serviceCost)) {
        throw new Error(`Service ${index}: Invalid service cost - got NaN from service_cost: ${service.service_cost}, service_amount: ${service.service_amount}`);
      }
      if (isNaN(serviceDiscount)) {
        throw new Error(`Service ${index}: Invalid service discount - got NaN from service_discount: ${service.service_discount}`);
      }
      if (isNaN(serviceTotal)) {
        throw new Error(`Service ${index}: Invalid service total - calculation resulted in NaN (${serviceCost} - ${serviceDiscount})`);
      }

      // Strip every derived pricing field before the spread. The UI state's
      // service_amount is NOT the gross amount — newServiceForm overwrites it
      // with cost − discount for the "Final Price" column — so letting it ride
      // along would send the server a wrong number with the right name.
      const {
        service_amount: _amount,
        service_value: _value,
        service_fee: _fee,
        ...inputs
      } = service;

      const preparedService = {
        ...inputs,
        id: service.id || null,
        quantity: quantity,
        unit_price: unitPrice,
        service_discount: serviceDiscount,
        // Marine: whole-job duration, NOT scaled by square footage. See
        // calculateTotalDuration above for why.
        service_duration: isMarineService(service)
          ? parseFloat(service.duration_days || 0)
          : parseFloat(service.duration_days || 0) * quantity,
        service_state: service.service_state || 'new',
        custom_description: service.description || service.custom_description || '',
        retail_price: service.retail_price ? parseFloat(service.retail_price) : null,
        // Marine phase 1 (Task 9): tells the server whether `quantity` is a unit
        // count (duration scales) or CEIL(sq ft) for an area-priced marine line
        // (duration does not scale). Always 'unit' until marine catalog rows exist.
        pricing_kind: isMarineService(service) ? 'area' : 'unit'
      };
      
      return preparedService;
    });
    
    return {
      services: preparedServices,
      // The four header money fields are REQUIRED on every request write (NOT
      // NULL columns; see the header note). Numbers, never undefined — the
      // breakdown yields 0 for an empty service list.
      calculatedTotals: {
        Request_Total: breakdown.originalTotal,
        Request_Discount: breakdown.totalDiscount,
        Request_Fee: breakdown.platformFee,
        Request_taxes: breakdown.taxes,
        request_duration: breakdown.totalDuration
      },
      breakdown: breakdown // Display / debugging only — never sent as pricing
    };
  }
}

// Legacy compatibility functions to replace existing serviceUtils
export const calculateRequestTotal = SharedRequestPricing.calculateRequestTotal;
export const calculateRequestDiscount = SharedRequestPricing.calculateRequestDiscount;
export const calculateFinalAmount = SharedServicePricing.calculateServiceTotal;